/**
 * A retry wrapper using exponential backoff for the provided function. The delay between
 * attempts looks like:
 *  1st: happens immediately
 *  2nd: waits 1 second after the first attempt
 *  3rd: waits 3 seconds after the second attempt
 *  4th: waits 7 seconds
 *  5th: waits 15 seconds
 *
 * 5 attempts means the function will be tried a maximum of 5 times, with having
 * spent upto 26s waiting.
 *
 * @param attempts - maximum number of attempts
 * @param fn - a function to retry
 * @param [params] - params to pass to function
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic utility needs any for arbitrary function signatures
export const retry = async function <T extends (...args: any[]) => any | (() => any)>(
    attempts: number,
    fn: T,
    ...params: Parameters<T>
): Promise<ReturnType<T>> {
    let error;
    for (let i = 0; i < attempts; i++) {
        const backoff = Math.pow(2, i) - 1;
        if (backoff > 0) {
            await new Promise((resolve) => setTimeout(resolve, backoff * 1000));
        }
        try {
            return await fn(...params);
        } catch (err) {
            error = err;
        }
    }
    console.warn(`Retry failed after ${attempts} attempts`);
    // oxlint-disable-next-line no-throw-literal -- re-throws the error captured from fn()
    throw error;
};

/**
 * Creates a validator, which wraps a function call with a successive call to a custom validate
 * function. If no errors are thrown, then it returns the result from the function call.
 *
 * This can be used to wrap the input function in retry (above) in order to add custom
 * criteria, which can further require additional retries.
 *
 * const validator = createValidator(customAssertFunction);
 * await retry(2, validator(functionToRetry));
 */
export const createValidator =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic utility needs any for arbitrary function signatures
    <T extends ((...args: any[]) => Promise<R>) | ((...args: any[]) => R), R>(
        validator: (data: R) => void,
    ) =>
        (func: T) =>
        async (...params: Parameters<T>): Promise<R> => {
            const result = await func(...params);
            validator(result);
            return result;
        };
